import { z } from 'zod';

export const storySchema = z.object({
  revision: z.number().int().nonnegative(),
  visible: z.boolean(),
  mode: z.enum(['slides', 'video']),
  slides: z.array(z.object({ assetId: z.string().uuid(), seconds: z.number().min(2).max(300) })).max(250),
  videoId: z.string().uuid().nullable(),
  tracks: z
    .array(
      z.object({
        assetId: z.string().uuid(),
        startSeconds: z.number().min(0).max(7200),
        offsetSeconds: z.number().min(0).max(7200),
        volume: z.number().min(0).max(1),
      }),
    )
    .max(30),
  videoSound: z.boolean(),
  musicOnVideo: z.boolean().default(false),
});
export type StorySettings = z.infer<typeof storySchema>;
export const storyAssetSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['slide', 'video', 'audio']),
  name: z.string().max(180),
  mime: z.enum(['image/webp', 'video/mp4', 'audio/mp4']),
  duration: z.number().finite().nonnegative(),
});
export type StoryAsset = z.infer<typeof storyAssetSchema>;
export type StoryDocument = { settings: StorySettings; assets: StoryAsset[] };
export const emptyStory: StorySettings = {
  revision: 0,
  visible: false,
  mode: 'slides',
  slides: [],
  videoId: null,
  tracks: [],
  videoSound: true,
  musicOnVideo: false,
};
export const storyAssetUrl = (id: string) => `/story-media/${id}`;
export const storyAssetIds = (s: StorySettings) => [
  ...s.slides.map((slide) => slide.assetId),
  ...(s.videoId ? [s.videoId] : []),
  ...s.tracks.map((track) => track.assetId),
];
export function activeStoryAssetIds(s: StorySettings) {
  return [
    ...(s.mode === 'slides' ? s.slides.map((slide) => slide.assetId) : s.videoId ? [s.videoId] : []),
    ...(s.mode === 'slides' || s.musicOnVideo ? s.tracks.map((track) => track.assetId) : []),
  ];
}
export function storyDuration(doc: StoryDocument) {
  return doc.settings.mode === 'slides'
    ? doc.settings.slides.reduce((total, slide) => total + slide.seconds, 0)
    : (doc.assets.find((asset) => asset.id === doc.settings.videoId)?.duration ?? 0);
}
export function storyPosition(slides: StorySettings['slides'], seconds: number) {
  let start = 0;
  for (let index = 0; index < slides.length; index++) {
    if (seconds < start + slides[index].seconds || index === slides.length - 1)
      return { index, start, progress: Math.min(1, Math.max(0, (seconds - start) / slides[index].seconds)) };
    start += slides[index].seconds;
  }
  return { index: 0, start: 0, progress: 0 };
}
